import * as React from 'react';
import * as R from 'ramda';

import { Form, Formik } from 'formik';
import { connect } from 'react-redux';
import * as yup from 'yup';

import Box from '@components/legos/primitives/Box';
import Header from '@components/legos/primitives/Header';
import { IRootState, ThunkDispatch } from '@src/domain';
import {
  getHasActiveIndividualLicense,
  getOrganizationsWithActiveSubscription,
} from '@src/domain/Organization/OrganizationSelectors';
import { routes } from '@src/domain/Ui/UiConstants';
import { deleteAccount } from '@src/domain/User/UserActions';
import { pluralize } from '@src/utils/stringUtils';
import { RouteChildrenProps } from 'react-router';
import Button from '../legos/Button';
import FormField from '../legos/forms/FormField';
import FormStatus, { IFormStatusObject } from '../legos/forms/FormStatus';
import Alert from '../legos/primitives/Alert';
import Text from '../legos/primitives/Text';
import { FormLabel } from '../legos/forms/FormLabel';
import NavButtonLink from '../legos/NavButtonLink';

const mapStateToProps = (state: IRootState) => ({
  activeIndividualOrg: getHasActiveIndividualLicense(state),
  activeOrgs: getOrganizationsWithActiveSubscription(state),
  user: state.User.user,
});

const mapDispatchToProps = (dispatch: ThunkDispatch, { history }: RouteChildrenProps) => ({
  deleteUser: () => dispatch(deleteAccount(history)),
});

const deleteUserDefaultValues = {
  deletionConfirmation: '',
};

type DeleteAccountProps =
  ReturnType<typeof mapDispatchToProps>
  & ReturnType<typeof mapStateToProps>
  & { onRequestClose: any }
  & RouteChildrenProps;

interface IDeleteAPIError {
  memberOf: string[];
  owned: {
    active: string[];
    hasMembers: string[];
  };
}

const DeleteUser: React.FC<DeleteAccountProps> = (props) => {
  const { deleteUser, activeIndividualOrg, activeOrgs, user, onRequestClose } = props;

  if (!user) {
    return (
      <React.Fragment>
        <p>Something&#39;s gone wrong!</p>
        <NavButtonLink to={routes.ACCOUNT_INFO.path} variant='plain'>Close</NavButtonLink>
      </React.Fragment>
    );
  }

  const DeleteAccountValidationSchema = yup.object().shape({
    deletionConfirmation: yup.string()
      .matches(/^delete my account$/i, 'Type the prompt exactly as shown to continue')
      .required('Type the prompt exactly as shown to continue'),
  });

  return (
    <Box minWidth='300px'>
      <Header mb={4}>Delete Your Account</Header>
      <Formik
        initialValues={deleteUserDefaultValues}
        onSubmit={async (values, { setStatus, setSubmitting }) => {
          try {
            await deleteUser();
          } catch (e) {
            let message: React.ReactNode;
            if (e.response.status === 409) {
              const errorData = R.path<IDeleteAPIError>(['response', 'data'], e);
              if (errorData) {
                message = (
                  <>
                    { errorData.owned.active.length > 0 &&
                      <Box>
                        The following organizations must be cancelled:&nbsp;
                        {errorData.owned.active.join(', ')}
                      </Box>
                    }
                    { errorData.owned.hasMembers.length > 0 &&
                      <Box mt={1}>
                        All members from the following organizations must be removed:&nbsp;
                        {errorData.owned.hasMembers.join(', ')}
                      </Box>
                    }
                    { errorData.memberOf.length > 0 &&
                      <Box mt={1}>
                        An administrator must remove you from the following organizations:&nbsp;
                        {errorData.memberOf.join(', ')}
                      </Box>
                    }
                  </>
                );
              }
            }

            setSubmitting(false);
            setStatus({
              message: message || e.message || 'An unexpected error occurred.',
              type: 'error',
            } as IFormStatusObject);
          }
        }}
        validationSchema={DeleteAccountValidationSchema}
      >
        {({ isValid, isSubmitting }) => (
          <Form autoComplete='off'>
            <Alert
              message={
                `You are deleting the account "${user.username}".
                If you continue with the deletion you won’t be able to use Gitkraken with this account.`
              }
              type='danger'
            />
            {activeIndividualOrg &&
              <Alert
                message={
                  'You have an active individual license. Your subscription will be canceled.'
                }
                type='warning'
              />
            }
            {activeOrgs.length > 0 &&
            <Alert
              message={
                `You have ${activeOrgs.length} active ${pluralize('subscription', 'subscriptions', activeOrgs.length)}.
                ${pluralize('The subscription', 'Subscriptions', activeOrgs.length)} will be canceled.`
              }
              type='warning'
            />
            }
            <FormLabel htmlFor='deletionConfirmation'>
              To verify, please type <Text color='text.selected'>&quot;Delete My Account&quot;</Text> below.
            </FormLabel>
            <FormField
              caption='This action can not be undone!'
              label=''
              mt={2}
              name='deletionConfirmation'
              placeholder='Delete My Account'
              type='text'
            />
            <FormStatus />
            <Button disabled={!isValid || isSubmitting} mr={2} type='submit' variant='danger'>
              Delete your account
            </Button>
            <Button onClick={onRequestClose} type='button' variant='plain'>Cancel</Button>
          </Form>)}
      </Formik>
    </Box>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(DeleteUser);
